import {actionTool, objectInput, readOnlyTool, readyForPerson, stringInput, waitForSecrets} from "@/functions/webMcpTool";
import {actionTargets, cleanText, isPersonOnly, isSecretInput, isVisible, labelFor, openDialog, pressElement, snapshotPage, waitForOutcome} from "@/functions/webMcpDom";
import {CATEGORY_SENTENCES, categoryFor, describeCurrentStep, isStepSettled} from "@/functions/webMcpStep";
import {TERMS_REASON} from "@/functions/webMcpToolsSignIn";
import {ToolError} from "@/functions/webMcp";

const MAX_LABEL_LENGTH = 110
const EXCLUDED_INPUTS = '[id^="captcha-"], [id^="test-code-"], .emergency-codes-container, .qr-code-container, .modal-backdrop, #loadingScreen, #fingerprint'

const clip = (text) => (text.length > MAX_LABEL_LENGTH ? `${text.slice(0, MAX_LABEL_LENGTH)}…` : text)

const mainArea = () => document.getElementById('mainContentArea') ?? document.body

const toolSuffix = (route) => {
	const path = (route.matched?.[route.matched.length - 1]?.path ?? route.path ?? '').replace(/\/:[^/]+/g, '')
	if (path === '/' || path === '') return 'sign_in'
	return path.replace(/^\//, '').replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '').toLowerCase()
}

const screenInputs = () => [...mainArea().querySelectorAll('input, select, textarea')]
	.filter((element) => element.id && !['hidden', 'submit', 'button', 'image', 'reset', 'file'].includes(element.type))
	.filter((element) => !element.disabled && !element.closest(EXCLUDED_INPUTS))
	.filter((element) => isVisible(element) || (['radio', 'checkbox'].includes(element.type) && isVisible(element.labels?.[0])))

const fillableInputs = () => screenInputs().filter((element) => !isSecretInput(element))

const secretInputs = () => screenInputs().filter(isSecretInput)

const radioGroupKey = (element) => element.name || element.closest('fieldset')?.id || element.id

const describeFields = () => {
	const fields = []
	const groups = new Map()
	fillableInputs().forEach((element) => {
		if (element.type === 'radio') {
			const key = radioGroupKey(element)
			if (!groups.has(key)) {
				const group = {key, kind: 'choice', label: cleanText(element.closest('fieldset')?.querySelector('legend')) || labelFor(element), options: []}
				groups.set(key, group)
				fields.push(group)
			}
			groups.get(key).options.push({label: labelFor(element), element})
			return
		}
		fields.push({
			key: element.id,
			kind: element.type === 'checkbox' ? 'checkbox' : element.tagName === 'SELECT' ? 'select' : 'text',
			label: labelFor(element),
			element,
		})
	})
	return fields
}

const schemaFor = (field) => {
	if (field.kind === 'choice') return stringInput(clip(field.label), {enum: field.options.map((option) => option.label)})
	if (field.kind === 'checkbox') return {type: 'boolean', description: clip(field.label)}
	if (field.kind === 'select') return stringInput(clip(field.label), {enum: [...field.element.options].map((option) => cleanText(option)).filter(Boolean)})
	return stringInput(clip(`${field.label}${field.element.type === 'password' ? ' Never returned or logged.' : ''}`), field.element.maxLength > 0 ? {maxLength: field.element.maxLength} : {})
}

const isRequired = (field) => (field.kind === 'choice' ? field.options.some((option) => option.element.required) : field.element.required)

const setField = (field, value) => {
	if (field.kind === 'choice') {
		const option = field.options.find((candidate) => candidate.label === value)
		if (!option) throw new ToolError(`"${value}" is not an option for ${field.label}.`, 'INVALID_INPUT')
		if (!option.element.checked) option.element.click()
		return
	}
	const element = field.element
	if (!element.isConnected) throw new ToolError('The page changed. Call get_auth_state and try again.', 'PAGE_CHANGED', true)
	if (field.kind === 'checkbox') {
		if (element.checked !== Boolean(value)) element.click()
		return
	}
	element.focus()
	if (field.kind === 'select') {
		const option = [...element.options].find((candidate) => cleanText(candidate) === value)
		if (!option) throw new ToolError(`"${value}" is not an option for ${field.label}.`, 'INVALID_INPUT')
		element.value = option.value
	} else {
		element.value = String(value ?? '')
		element.dispatchEvent(new Event('input', {bubbles: true}))
	}
	element.dispatchEvent(new Event('change', {bubbles: true}))
	element.blur()
}

const primarySubmit = () => {
	const buttons = [...document.querySelectorAll('button[type="submit"]')].filter((button) => isVisible(button) && !button.closest('.modal-backdrop'))
	return buttons.find((button) => mainArea().contains(button)) ?? buttons[0]
}

const buildFillScreen = ({route}) => {
	const fields = describeFields()
	if ((!fields.length && !secretInputs().length) || !primarySubmit()) return []
	const heading = cleanText(mainArea().querySelector('h1')) || 'This screen'
	const submitText = cleanText(primarySubmit())
	const category = categoryFor(route.name)
	const name = `fill_${toolSuffix(route)}`
	const secretNote = secretInputs().length ? ' Passwords and codes are not parameters: they go into the page from the person or their password manager, and the tool waits until those fields are filled.' : ''
	return actionTool({
		name,
		description: `${heading}. ${CATEGORY_SENTENCES[category]} Fills this screen with the values you pass, keyed by field id, then presses "${submitText}" so the site's own checks and captcha run as they do for the person.${secretNote} Returns the next screen, or the messages that stopped it. Field values are never returned or logged.`,
		inputSchema: objectInput(Object.fromEntries(fields.map((field) => [field.key, schemaFor(field)])), fields.filter(isRequired).map((field) => field.key)),
		run: (input) => {
			const current = describeFields()
			current.filter((field) => field.key in input).forEach((field) => setField(field, input[field.key]))
			const button = primarySubmit()
			if (!button) throw new ToolError('This screen has no submit button right now. Call get_auth_state to see what is showing.', 'PAGE_CHANGED', true)
			if (isPersonOnly(button)) return readyForPerson(button, {secrets: secretInputs(), reason: TERMS_REASON})
			const waiting = waitForSecrets(name, secretInputs())
			if (waiting) return waiting
			return pressElement(button, `"${cleanText(button)}" button`)
		},
	})
}

const notShowing = (action, showing) => new ToolError(`"${action}" is not showing${openDialog() ? ' in the open dialog' : ''} right now. Showing: ${showing.join('; ') || 'nothing'}.`, 'NOT_AVAILABLE', true)

const buildChooseAction = () => {
	const labels = actionTargets().filter(({display}) => !display).map(({label}) => label)
	if (!labels.length) return []
	return actionTool({
		name: 'choose_action',
		description: 'Press one of the buttons or links showing on this screen, by its visible label: for example "Create account", "Forgot username or password", "Resend code", or a choice in a dialog that just opened. Buttons that accept the Terms of Use, confirm an irreversible link, or reveal a password are left out; those are the person\'s. Returns the next screen, a dialog, or the page\'s message.',
		inputSchema: objectInput({
			action: stringInput('The button or link label, exactly as listed.', {enum: labels}),
		}, ['action']),
		run: ({action}) => {
			const targets = actionTargets().filter(({display}) => !display)
			const target = targets.find(({label}) => label === action)
			if (!target) throw notShowing(action, targets.map(({label}) => label))
			return pressElement(target.element, `"${action}" ${target.element.tagName === 'A' ? 'link' : 'button'}`)
		},
	})
}

const buildOpenOrCloseDialog = () => {
	const labels = actionTargets().filter(({display}) => display).map(({label}) => label)
	if (!labels.length) return []
	return readOnlyTool({
		name: 'open_or_close_dialog',
		description: 'Open or close an information dialog on this screen by its visible label, such as "Terms of Use", "Privacy Policy" or a dialog\'s Close button, so the person can read it on the page. Nothing is submitted or changed on the account. Returns dialog_opened or dialog_closed with the screen; get_legal_document reads the full text.',
		untrusted: true,
		inputSchema: objectInput({
			action: stringInput('The label, exactly as listed.', {enum: labels}),
		}, ['action']),
		execute: async ({action}, context) => {
			const targets = actionTargets().filter(({display}) => display)
			const target = targets.find(({label}) => label === action)
			if (!target) throw notShowing(action, targets.map(({label}) => label))
			const start = snapshotPage()
			pressElement(target.element, `"${action}" button`)
			return waitForOutcome({describeStep: describeCurrentStep, isSettled: isStepSettled, start, signal: context?.signal})
		},
	})
}

export const buildGeneratedTools = ({route, hasCuratedTools}) => [
	buildChooseAction(),
	buildOpenOrCloseDialog(),
	...(hasCuratedTools ? [] : [buildFillScreen({route})]),
].flat()